import * as React from "react";

/**
 * "Signal field" ambient background for the Ink panel — soft glows, a faint
 * grid, and a slow-drifting particle field with proximity-based connecting
 * lines. Adapted from a design-system artifact (AmbientPanel.jsx) supplied
 * directly for this panel; this is a drop-in replacement for
 * NetworkBackground.tsx in the same slot — same className-driven sizing
 * (absolute inset-0 h-full w-full inside the existing bg-ink-900 panel),
 * same "the caller lays content on top of it" contract. The testimonial
 * blockquote on LoginForm.tsx's right panel is unaffected; only this
 * background layer is being swapped.
 *
 * Colors: three of the four particle colors, and the panel's own glows,
 * match existing brand tokens exactly (--signal-cyan/--signal-magenta/
 * --signal-amber, see index.css) and are read live via readHsl so they'd
 * follow any future token update automatically. The fourth particle color
 * (a periwinkle) and the grid/connecting-line color (a cool slate) have no
 * corresponding brand token today — same "no brand token; left as-is"
 * situation index.css already tolerates for --border/--muted-foreground —
 * so they're kept as the literal values the supplied artifact specified
 * rather than inventing new tokens unprompted.
 */

// Read the resolved value of an "H S% L%" CSS custom property (see
// index.css) and wrap it as an hsl() string canvas can use directly. Mirrors
// NetworkBackground.tsx's own helper of the same name.
function readHsl(varName: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return raw ? `hsl(${raw})` : fallback;
}

interface SignalParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  color: string;
  a: number;
}

// Cool slate used for the grid texture and the particles' connecting lines —
// no matching brand token (see header comment); NetworkBackground's own
// lines use --paper-50 instead, a deliberately different, warmer look, so
// this isn't unified with that either.
const LINE_RGB = "139, 147, 173";
const PARTICLE_AREA = 11500;

function SignalField() {
  const ref = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const cnv = ref.current;
    if (!cnv) return;
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const ctx = cnv.getContext("2d");
    if (!ctx) return;

    const COLORS = [
      readHsl("--signal-cyan", "#2BC6E8"),
      readHsl("--signal-magenta", "#F0399A"),
      readHsl("--signal-amber", "#FFB020"),
      "#8B7BF0",
    ];

    let w = 0;
    let h = 0;
    let raf = 0;
    let parts: SignalParticle[] = [];

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = cnv.clientWidth;
      h = cnv.clientHeight;
      cnv.width = w * dpr;
      cnv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.round((w * h) / PARTICLE_AREA);
      parts = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.16,
        vy: (Math.random() - 0.5) * 0.16,
        r: Math.random() * 1.6 + 0.8,
        color: COLORS[(Math.random() * COLORS.length) | 0],
        a: Math.random() * 0.4 + 0.12,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -20) p.x = w + 20;
        if (p.x > w + 20) p.x = -20;
        if (p.y < -20) p.y = h + 20;
        if (p.y > h + 20) p.y = -20;
        for (let j = i + 1; j < parts.length; j++) {
          const q = parts[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d = Math.hypot(dx, dy);
          if (d < 128) {
            ctx.strokeStyle = `rgba(${LINE_RGB}, ${(1 - d / 128) * 0.1})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
      }
      // Per-particle alpha via globalAlpha rather than embedding it in the
      // color string — same approach NetworkBackground.tsx uses, and safer
      // than relying on `hsl(H S% L% / A)` canvas support for the hex
      // fallback color.
      for (const p of parts) {
        ctx.globalAlpha = p.a;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };

    resize();
    tick();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="absolute inset-0 h-full w-full"
    />
  );
}

export function AmbientPanel({ className }: { className?: string }) {
  return (
    <div className={className} aria-hidden="true">
      {/* soft glows */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-16%",
          right: "-8%",
          width: 620,
          height: 620,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, hsl(var(--signal-cyan) / 0.22), transparent 66%)",
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: "-22%",
          left: "-14%",
          width: 560,
          height: 560,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, hsl(var(--signal-magenta) / 0.14), transparent 68%)",
        }}
      />
      {/* subtle grid texture */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(rgba(${LINE_RGB}, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(${LINE_RGB}, 0.06) 1px, transparent 1px)`,
          backgroundSize: "168px 168px",
          maskImage:
            "radial-gradient(120% 100% at 50% 40%, #000 55%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(120% 100% at 50% 40%, #000 55%, transparent 100%)",
        }}
      />
      {/* slow signal-particle field */}
      <SignalField />
      {/* protection gradient, so text laid on top of this panel stays legible */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(120% 90% at 30% 45%, hsl(var(--ink-900) / 0.10), hsl(var(--ink-900) / 0.55) 82%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

export default AmbientPanel;
